import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

import "./CompanyProducts.css";

import DataTable from "./components/DataTable.jsx";
import ProductVariantsModal from "./components/ProductVariantsModal";
import { usePermissions } from "./PermissionContext";
import { API_URL, getPosHeaders } from "./api";

const emptyProduct = {
  sku: "",
  name: "",
  description: "",
  category: "",
  tax_group_id: "",
  purchase_price: "",
  sale_price: "",
  minimum_stock: 0,
};

function formatCurrency(value) {
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function getErrorMessage(error, fallback) {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") {
    return detail;
  }

  if (Array.isArray(detail)) {
    return detail.map((item) => item.msg).join(". ");
  }

  if (error.response?.status === 401) {
    return "La sesión POS venció. Inicia sesión nuevamente.";
  }

  if (error.response?.status === 403) {
    return "No tienes permiso para realizar esta operación.";
  }

  return fallback || "No fue posible completar la operación.";
}

function getResponseItems(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.products)) return data.products;

  return [];
}

function escapeHtml(value) {
  return String(value ?? "").replace(
    /[&<>"']/g,
    (character) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[character]
  );
}

function getTaxTreatmentLabel(treatment) {
  const labels = {
    taxable: "Gravado",
    excluded: "Excluido de IVA",
    exempt: "Exento de IVA",
    not_applicable: "No aplica",
  };

  return labels[treatment] || "Sin definir";
}

function buildProductPayload(product) {
  return {
    sku: String(product.sku || "").trim(),
    name: String(product.name || "").trim(),
    description:
      String(product.description || "").trim() || null,
    category:
      String(product.category || "").trim() || null,
    tax_group_id: Number(product.tax_group_id),
    purchase_price: Number(product.purchase_price),
    sale_price: Number(product.sale_price),
    minimum_stock: Number(product.minimum_stock),
  };
}

function validateProduct(product) {
  if (!String(product.sku || "").trim()) {
    return "El código o SKU es obligatorio.";
  }

  if (!String(product.name || "").trim()) {
    return "El nombre del producto es obligatorio.";
  }

  if (
    !product.tax_group_id ||
    !Number.isInteger(Number(product.tax_group_id)) ||
    Number(product.tax_group_id) <= 0
  ) {
    return "Debes seleccionar un grupo tributario.";
  }

  if (
    product.purchase_price === "" ||
    product.purchase_price == null ||
    !Number.isFinite(Number(product.purchase_price)) ||
    Number(product.purchase_price) < 0
  ) {
    return "El precio de compra debe ser mayor o igual a cero.";
  }

  if (
    product.sale_price === "" ||
    product.sale_price == null ||
    !Number.isFinite(Number(product.sale_price)) ||
    Number(product.sale_price) <= 0
  ) {
    return "El precio de venta debe ser mayor a cero.";
  }

  if (
    product.minimum_stock === "" ||
    product.minimum_stock == null ||
    !Number.isInteger(Number(product.minimum_stock)) ||
    Number(product.minimum_stock) < 0
  ) {
    return "El stock mínimo debe ser un número entero mayor o igual a cero.";
  }

  return "";
}

function TaxPreview({ information }) {
  const { group, rule } = information;

  if (!group || !rule) {
    return (
      <div className="tax-preview tax-preview-empty">
        Selecciona un grupo tributario para conocer el impuesto.
      </div>
    );
  }

  return (
    <div className="tax-preview">
      <div>
        <span>Tratamiento tributario</span>
        <strong>
          {getTaxTreatmentLabel(rule.treatment)}
        </strong>
      </div>

      <div>
        <span>Tarifa</span>
        <strong>{Number(rule.rate || 0)}%</strong>
      </div>

      <p>
        {rule.description ||
          group.description ||
          "Regla tributaria configurada para el grupo."}
      </p>
    </div>
  );
}

function CategorySearch({
  prefix,
  value,
  categories,
  onChange,
  disabled,
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const listRef = useRef(null);

  const inputId = `${prefix}-category`;
  const listId = `${prefix}-category-results`;

  const normalizeText = (text) =>
    String(text || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  const term = normalizeText(value.trim());

  const results = term
    ? [...new Set(categories)].filter((category) =>
      normalizeText(category).includes(term)
    )
    : [];

  const showResults = open && Boolean(term) && !disabled;

  useEffect(() => {
    if (showResults && activeIndex >= 0) {
      listRef.current
        ?.querySelector('[aria-selected="true"]')
        ?.scrollIntoView({
          block: "nearest",
        });
    }
  }, [activeIndex, showResults]);

  function selectCategory(category) {
    onChange({
      target: {
        name: "category",
        value: category,
      },
    });

    setOpen(false);
    setActiveIndex(-1);
  }

  function handleChange(event) {
    onChange(event);
    setActiveIndex(-1);
    setOpen(Boolean(event.target.value.trim()));
  }

  function handleKeyDown(event) {
    if (event.key === "Escape") {
      if (showResults) {
        event.preventDefault();
        event.stopPropagation();
      }

      setOpen(false);
      setActiveIndex(-1);
      return;
    }

    if (!term || disabled) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);

      setActiveIndex((current) =>
        results.length
          ? Math.min(current + 1, results.length - 1)
          : -1
      );
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);

      setActiveIndex((current) =>
        results.length
          ? current <= 0
            ? results.length - 1
            : current - 1
          : -1
      );
    }

    if (event.key === "Enter" && showResults) {
      // Evita enviar el formulario al elegir una sugerencia.
      event.preventDefault();

      if (activeIndex >= 0 && results[activeIndex]) {
        selectCategory(results[activeIndex]);
      }
    }
  }

  return (
    <div className="product-category-search">
      <div className="floating-field">
        <input
          id={inputId}
          name="category"
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={showResults}
          aria-controls={showResults ? listId : undefined}
          aria-activedescendant={
            showResults && activeIndex >= 0
              ? `${listId}-${activeIndex}`
              : undefined
          }
          placeholder=" "
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            setOpen(false);
            setActiveIndex(-1);
          }}
          autoComplete="off"
          disabled={disabled}
        />

        <label htmlFor={inputId}>
          Buscar categoría
        </label>
      </div>

      {showResults && (
        <div className="product-category-dropdown">
          <div className="product-category-heading">
            <strong>Resultados de búsqueda</strong>

            <span role="status">
              {results.length} encontradas
            </span>
          </div>

          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            aria-label="Categorías encontradas"
            className="product-category-list"
          >
            {results.map((category, index) => (
              <li
                key={category}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={activeIndex === index}
                className={`product-category-option${activeIndex === index ? " is-active" : ""
                  }`}
                onMouseDown={(event) => {
                  // Conserva el foco hasta completar la selección.
                  event.preventDefault();
                }}
                onClick={() => selectCategory(category)}
              >
                <span
                  className="product-category-avatar"
                  aria-hidden="true"
                >
                  {category.trim().charAt(0).toUpperCase()}
                </span>

                <span className="product-category-information">
                  <strong>{category}</strong>
                  <small>Categoría de productos</small>
                </span>

                <span
                  className="product-category-arrow"
                  aria-hidden="true"
                >
                  ›
                </span>
              </li>
            ))}
          </ul>

          {results.length === 0 && (
            <div
              className="product-category-empty"
              role="status"
            >
              No se encontraron categorías con ese nombre.
            </div>
          )}

          <div className="product-category-footer">
            ↑ ↓ Seleccionar · Enter Elegir · Esc Cerrar
          </div>
        </div>
      )}
    </div>
  );
}
function ProductFields({
  prefix,
  product,
  categories,
  taxGroups,
  onChange,
  taxInformation,
  disabled,
}) {
  const currentCategory = product.category || "";

  return (
    <>
      <div className="product-form-grid">
        <div className="floating-field">
          <input
            type="text"
            id={`${prefix}-sku`}
            name="sku"
            placeholder=" "
            value={product.sku}
            onChange={onChange}
            disabled={disabled}
            required
          />

          <label htmlFor={`${prefix}-sku`}>
            Código o SKU
          </label>
        </div>

        <div className="floating-field">
          <input
            type="text"
            id={`${prefix}-name`}
            name="name"
            placeholder=" "
            value={product.name}
            onChange={onChange}
            disabled={disabled}
            required
          />

          <label htmlFor={`${prefix}-name`}>
            Nombre del producto
          </label>
        </div>

        <div className="floating-field">
          <CategorySearch
            prefix={prefix}
            value={currentCategory}
            categories={categories}
            onChange={onChange}
            disabled={disabled}
          />
        </div>

        <div className="floating-field">
          <select
            id={`${prefix}-tax-group`}
            name="tax_group_id"
            value={product.tax_group_id || ""}
            onChange={onChange}
            disabled={disabled}
            required
          >
            <option value="">
              Selecciona el tratamiento de IVA
            </option>

            {product.tax_group_id &&
              !taxGroups.some(
                (group) =>
                  String(group.id) ===
                  String(product.tax_group_id)
              ) && (
                <option value={product.tax_group_id}>
                  Configuración anterior · Revisar tratamiento
                </option>
              )}

            {taxGroups.map((group) => (
              <option
                key={group.id}
                value={group.id}
              >
                {group.name}
              </option>
            ))}
          </select>

          <label htmlFor={`${prefix}-tax-group`}>
            Tratamiento de IVA
          </label>
        </div>

        <div className="floating-field">
          <input
            type="number"
            id={`${prefix}-minimum-stock`}
            name="minimum_stock"
            placeholder=" "
            min="0"
            step="1"
            value={product.minimum_stock}
            onChange={onChange}
            disabled={disabled}
            required
          />

          <label htmlFor={`${prefix}-minimum-stock`}>
            Stock mínimo
          </label>
        </div>

        <div className="floating-field">
          <input
            type="number"
            id={`${prefix}-purchase-price`}
            name="purchase_price"
            placeholder=" "
            min="0"
            step="0.01"
            value={product.purchase_price}
            onChange={onChange}
            disabled={disabled}
            required
          />

          <label htmlFor={`${prefix}-purchase-price`}>
            Precio de compra
          </label>
        </div>

        <div className="floating-field">
          <input
            type="number"
            id={`${prefix}-sale-price`}
            name="sale_price"
            placeholder=" "
            min="0.01"
            step="0.01"
            value={product.sale_price}
            onChange={onChange}
            disabled={disabled}
            required
          />

          <label htmlFor={`${prefix}-sale-price`}>
            Precio de venta
          </label>
        </div>
      </div>

      <TaxPreview information={taxInformation} />

      <div className="floating-field floating-textarea">
        <textarea
          id={`${prefix}-description`}
          name="description"
          placeholder=" "
          value={product.description}
          onChange={onChange}
          disabled={disabled}
          rows="3"
        />

        <label htmlFor={`${prefix}-description`}>
          Descripción del producto
        </label>
      </div>
    </>
  );
}

export default function CompanyProducts() {
  const { company } = useParams();
  const { can, loadingPermissions } = usePermissions();

  const canViewProducts = can("products.view");
  const canManageProducts = can("products.manage");

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [categories, setCategories] = useState([]);
  const [taxGroups, setTaxGroups] = useState([]);
  const [taxRules, setTaxRules] = useState([]);
  const [catalogError, setCatalogError] = useState("");

  const [formData, setFormData] = useState({
    ...emptyProduct,
  });

  const [isCreating, setIsCreating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const [
    selectedProductForVariants,
    setSelectedProductForVariants,
  ] = useState(null);

  const fileInputRef = useRef(null);
  const requestVersionRef = useRef(0);
  const mutationRef = useRef(false);

  const getHeaders = useCallback(
    () => ({
      headers: getPosHeaders(company),
    }),
    [company]
  );

  const getVariantHeaders = useCallback(
    () => getPosHeaders(company),
    [company]
  );

  const fetchProducts = useCallback(async () => {
    const response = await axios.get(
      `${API_URL}/products/pos/me`,
      getHeaders()
    );

    return getResponseItems(response.data);
  }, [getHeaders]);

  const fetchTaxCatalog = useCallback(async () => {
    const [taxResponse, categoriesResponse] = await Promise.all([
      axios.get(
        `${API_URL}/product-tax/pos/me/catalog`,
        getHeaders()
      ),
      axios.get(
        `${API_URL}/pos/categories/me`,
        {
          ...getHeaders(),
          params: {
            active_only: true,
          },
        }
      ),
    ]);

    return {
      ...taxResponse.data,

      categories: categoriesResponse.data.map(
        (category) => category.name
      ),
    };
  }, [getHeaders]);

  const loadInitialData = useCallback(async () => {
    const version = ++requestVersionRef.current;

    if (loadingPermissions) return;

    if (!canViewProducts) {
      setProducts([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError("");
    setCatalogError("");

    const [productsResult, catalogResult] =
      await Promise.allSettled([
        fetchProducts(),
        fetchTaxCatalog(),
      ]);

    if (version !== requestVersionRef.current) return;

    if (productsResult.status === "fulfilled") {
      setProducts(productsResult.value);
    } else {
      const message = getErrorMessage(
        productsResult.reason,
        "No fue posible cargar los productos."
      );

      setLoadError(message);
      toast.error(message);
    }

    if (catalogResult.status === "fulfilled") {
      const data = catalogResult.value || {};

      setCategories(
        Array.isArray(data.categories)
          ? data.categories
          : []
      );

      setTaxGroups(
        Array.isArray(data.tax_groups)
          ? data.tax_groups
          : []
      );

      setTaxRules(
        Array.isArray(data.tax_rules)
          ? data.tax_rules
          : []
      );
    } else {
      setCategories([]);
      setTaxGroups([]);
      setTaxRules([]);

      setCatalogError(
        getErrorMessage(
          catalogResult.reason,
          "No fue posible cargar el catálogo tributario."
        )
      );
    }

    setLoading(false);
  }, [
    loadingPermissions,
    canViewProducts,
    fetchProducts,
    fetchTaxCatalog,
  ]);

  useEffect(() => {
    setProducts([]);
    setFormData({ ...emptyProduct });
    setShowEditModal(false);
    setEditingProduct(null);
    setSelectedProductForVariants(null);

    loadInitialData();

    return () => {
      requestVersionRef.current += 1;
    };
  }, [loadInitialData]);

  const refreshProductsAfterMutation = useCallback(
    async () => {
      try {
        const items = await fetchProducts();

        setProducts(items);
        setLoadError("");
      } catch (error) {
        setLoadError(
          getErrorMessage(
            error,
            "No fue posible actualizar el listado."
          )
        );

        toast.warning(
          "La operación terminó, pero no se pudo actualizar " +
          "el listado. Presiona Actualizar."
        );
      }
    },
    [fetchProducts]
  );

  const getTaxInformation = useCallback(
    (taxGroupId) => {
      if (!taxGroupId) {
        return { group: null, rule: null };
      }

      const group = taxGroups.find(
        (item) =>
          Number(item.id) === Number(taxGroupId)
      );

      if (!group) {
        return { group: null, rule: null };
      }

      const rule = taxRules.find(
        (item) =>
          Number(item.id) === Number(group.tax_rule_id)
      );

      return {
        group,
        rule: rule || null,
      };
    },
    [taxGroups, taxRules]
  );

  const createTaxInformation = useMemo(
    () => getTaxInformation(formData.tax_group_id),
    [formData.tax_group_id, getTaxInformation]
  );

  const editTaxInformation = useMemo(
    () => getTaxInformation(editingProduct?.tax_group_id),
    [editingProduct?.tax_group_id, getTaxInformation]
  );

  const handleCreateChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleCreateProduct = async (event) => {
    event.preventDefault();

    if (mutationRef.current) return;

    const validationError = validateProduct(formData);

    if (validationError) {
      toast.error(validationError);
      return;
    }

    mutationRef.current = true;
    setIsCreating(true);

    try {
      await axios.post(
        `${API_URL}/products/pos/me`,
        buildProductPayload(formData),
        getHeaders()
      );

      toast.success("Producto creado correctamente.");

      setFormData({ ...emptyProduct });

      await refreshProductsAfterMutation();
    } catch (error) {
      toast.error(
        getErrorMessage(
          error,
          "No fue posible crear el producto."
        )
      );
    } finally {
      mutationRef.current = false;
      setIsCreating(false);
    }
  };

  const downloadProductsTemplate = async () => {
    if (isDownloading || mutationRef.current) return;

    setIsDownloading(true);

    try {
      const response = await axios.get(
        `${API_URL}/products/pos/template`,
        {
          ...getHeaders(),
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");

      link.href = url;
      link.download = "plantilla_productos.xlsx";

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.setTimeout(
        () => window.URL.revokeObjectURL(url),
        1000
      );
    } catch (error) {
      let readableError = error;

      // Axios devuelve también los errores como Blob
      // cuando se solicita responseType: "blob".
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const data = JSON.parse(text);

          readableError = {
            response: {
              status: error.response.status,
              data,
            },
          };
        } catch {
          // Conserva el error original si no contiene JSON.
        }
      }

      toast.error(
        getErrorMessage(
          readableError,
          "No fue posible descargar la plantilla Excel."
        )
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const importProductsExcel = async (event) => {
    const input = event.currentTarget;
    const file = input.files?.[0];

    if (!file) return;

    if (mutationRef.current) {
      input.value = "";
      return;
    }

    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      toast.error(
        "Selecciona un archivo Excel con extensión .xlsx."
      );

      input.value = "";
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("El archivo no puede superar los 5 MB.");

      input.value = "";
      return;
    }

    const excelData = new FormData();
    excelData.append("file", file);

    mutationRef.current = true;
    setIsImporting(true);

    try {
      const response = await axios.post(
        `${API_URL}/products/pos/me/import-excel`,
        excelData,
        getHeaders()
      );

      toast.success(
        response.data.message ||
        `${response.data.created} productos importados correctamente.`
      );

      await refreshProductsAfterMutation();
    } catch (error) {
      toast.error(
        getErrorMessage(
          error,
          "No fue posible importar el archivo Excel."
        )
      );
    } finally {
      mutationRef.current = false;
      setIsImporting(false);
      input.value = "";
    }
  };

  const openEditModal = useCallback(
    (product) => {
      if (!canManageProducts || mutationRef.current) {
        return;
      }

      setEditingProduct({
        id: product.id,
        sku: product.sku || "",
        name: product.name || "",
        description: product.description || "",
        category: product.category || "",
        tax_group_id: product.tax_group_id
          ? String(product.tax_group_id)
          : "",
        purchase_price: product.purchase_price ?? "",
        sale_price: product.sale_price ?? "",
        minimum_stock: product.minimum_stock ?? 0,
        is_active: product.is_active,
      });

      setShowEditModal(true);
    },
    [canManageProducts]
  );

  const closeEditModal = useCallback(() => {
    if (mutationRef.current) return;

    setShowEditModal(false);
    setEditingProduct(null);
  }, []);

  useEffect(() => {
    if (!showEditModal) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function handleEscape(event) {
      if (event.key === "Escape") {
        closeEditModal();
      }
    }

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;

      document.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, [showEditModal, closeEditModal]);

  const openVariantsModal = () => {
    if (
      !editingProduct ||
      !canManageProducts ||
      mutationRef.current
    ) {
      return;
    }

    setShowEditModal(false);

    setSelectedProductForVariants({
      id: editingProduct.id,
      name: editingProduct.name,
      sku: editingProduct.sku,
      sale_price: editingProduct.sale_price,
      is_active: editingProduct.is_active,
    });
  };

  const handleEditChange = (event) => {
    const { name, value } = event.target;

    setEditingProduct((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const saveProduct = async (event) => {
    event.preventDefault();

    if (!editingProduct || mutationRef.current) return;

    if (!canManageProducts) {
      toast.error(
        "No tienes permiso para administrar productos."
      );
      return;
    }

    const validationError = validateProduct(editingProduct);

    if (validationError) {
      toast.error(validationError);
      return;
    }

    mutationRef.current = true;
    setIsSaving(true);

    try {
      await axios.put(
        `${API_URL}/products/pos/${editingProduct.id}`,
        buildProductPayload(editingProduct),
        getHeaders()
      );

      toast.success("Ficha del producto actualizada.");

      setShowEditModal(false);
      setEditingProduct(null);

      await refreshProductsAfterMutation();
    } catch (error) {
      toast.error(
        getErrorMessage(
          error,
          "No fue posible actualizar el producto."
        )
      );
    } finally {
      mutationRef.current = false;
      setIsSaving(false);
    }
  };

  const toggleProductStatus = useCallback(
    async (product) => {
      if (mutationRef.current) return;

      if (!canManageProducts) {
        toast.error(
          "No tienes permiso para administrar productos."
        );
        return;
      }

      const newStatus = !product.is_active;
      const action = newStatus ? "activar" : "inactivar";

      if (
        !window.confirm(
          `¿Deseas ${action} el producto "${product.name}"?`
        )
      ) {
        return;
      }

      mutationRef.current = true;

      try {
        await axios.patch(
          `${API_URL}/products/pos/${product.id}/status`,
          {
            is_active: newStatus,
          },
          getHeaders()
        );

        toast.success(
          `Producto ${newStatus ? "activado" : "inactivado"
          } correctamente.`
        );

        await refreshProductsAfterMutation();
      } catch (error) {
        toast.error(
          getErrorMessage(
            error,
            `No fue posible ${action} el producto.`
          )
        );
      } finally {
        mutationRef.current = false;
      }
    },
    [
      canManageProducts,
      getHeaders,
      refreshProductsAfterMutation,
    ]
  );

  const columns = useMemo(
    () => [
      {
        title: "Código",
        data: "sku",
        render: (value) => escapeHtml(value),
      },
      {
        title: "Producto",
        data: "name",
        render: (value) => escapeHtml(value),
      },
      {
        title: "Categoría",
        data: "category",
        render: (category) =>
          escapeHtml(category || "Sin categoría"),
      },
      {
        title: "Impuesto",
        data: "tax_rule_name",
        render: (_, __, row) => {
          if (!row.tax_rule_name) {
            return (
              '<span class="tax-badge tax-pending">' +
              "Sin configurar</span>"
            );
          }

          const taxClass =
            {
              taxable: "tax-taxable",
              excluded: "tax-excluded",
              exempt: "tax-exempt",
              not_applicable: "tax-not-applicable",
            }[row.tax_treatment] || "";

          const rate =
            Number(row.tax_rate || 0) > 0
              ? ` · ${Number(row.tax_rate)}%`
              : "";

          return (
            `<span class="tax-badge ${taxClass}">` +
            `${escapeHtml(row.tax_rule_name)}${rate}` +
            "</span>"
          );
        },
      },
      {
        title: "Compra",
        data: "purchase_price",
        render: (price) => formatCurrency(price),
      },
      {
        title: "Venta",
        data: "sale_price",
        render: (price) => formatCurrency(price),
      },
      {
        title: "Stock mínimo",
        data: "minimum_stock",
        render: (value) => Number(value || 0),
      },
      {
        title: "Estado",
        data: "is_active",
        render: (isActive) =>
          isActive
            ? '<span class="status active">Activo</span>'
            : '<span class="status inactive">Inactivo</span>',
      },
    ],
    []
  );

  const activeProducts = products.filter(
    (product) => product.is_active
  ).length;

  const inactiveProducts = products.filter(
    (product) => !product.is_active
  ).length;

  const configuredProducts = products.filter(
    (product) => product.tax_group_id
  ).length;

  const busy = isCreating || isSaving || isImporting;

  if (loadingPermissions) {
    return (
      <div className="company-products" role="status">
        Validando sesión POS…
      </div>
    );
  }

  if (!canViewProducts) {
    return (
      <div className="alert alert-warning" role="alert">
        No tienes permiso para consultar productos.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="company-products" aria-busy="true">
        <section className="products-heading">
          <div>
            <h1>Productos</h1>

            <p role="status">
              Cargando catálogo de productos…
            </p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="company-products">
      <div className="products-heading">
        <div>
          <h1>Productos</h1>

          <p>
            Gestiona las fichas, precios, impuestos y estado
            de tus productos.
          </p>

          <div className="product-import-actions">
            <button
              type="button"
              className="btn-download-template"
              onClick={downloadProductsTemplate}
              disabled={isDownloading || busy}
            >
              {isDownloading
                ? "Descargando..."
                : "Descargar plantilla Excel"}
            </button>

            <button
              type="button"
              className="btn-import-excel"
              onClick={() =>
                fileInputRef.current?.click()
              }
              disabled={busy}
            >
              {isImporting
                ? "Importando..."
                : "Importar productos desde Excel"}
            </button>

            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              hidden
              onChange={importProductsExcel}
            />
          </div>
        </div>

        <div className="products-summary">
          <strong>{products.length}</strong>
          <span>Productos registrados</span>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-danger" role="alert">
          {loadError}
        </div>
      )}

      {catalogError && (
        <div className="alert alert-warning" role="alert">
          {catalogError} El formulario necesita el catálogo
          tributario para crear o editar productos.
        </div>
      )}

      <section
        className="products-overview"
        aria-label="Resumen del catálogo"
      >
        <article className="products-overview-card">
          <span
            className="products-overview-icon"
            aria-hidden="true"
          >
            ▦
          </span>

          <div>
            <span>Total de productos</span>
            <strong>{products.length}</strong>
            <small>Referencias registradas</small>
          </div>
        </article>

        <article className="products-overview-card">
          <span
            className="products-overview-icon products-overview-icon-success"
            aria-hidden="true"
          >
            ✓
          </span>

          <div>
            <span>Activos</span>
            <strong>{activeProducts}</strong>
            <small>Productos habilitados</small>
          </div>
        </article>

        <article className="products-overview-card">
          <span
            className="products-overview-icon products-overview-icon-muted"
            aria-hidden="true"
          >
            −
          </span>

          <div>
            <span>Inactivos</span>
            <strong>{inactiveProducts}</strong>
            <small>Productos deshabilitados</small>
          </div>
        </article>

        <article className="products-overview-card">
          <span
            className="products-overview-icon"
            aria-hidden="true"
          >
            %
          </span>

          <div>
            <span>Con grupo tributario</span>
            <strong>{configuredProducts}</strong>
            <small>Referencias con grupo asignado</small>
          </div>
        </article>
      </section>

      <form
        id="product-create-form"
        className="product-form"
        onSubmit={handleCreateProduct}
      >
        <h2>Crear producto</h2>

        <ProductFields
          prefix="create"
          product={formData}
          categories={categories}
          taxGroups={taxGroups}
          onChange={handleCreateChange}
          taxInformation={createTaxInformation}
          disabled={busy || Boolean(catalogError)}
        />

        <button
          type="submit"
          disabled={
            busy ||
            Boolean(catalogError) ||
            taxGroups.length === 0
          }
        >
          {isCreating
            ? "Creando producto..."
            : "Crear producto"}
        </button>
      </form>

      <section className="products-table">
        <div className="products-table-heading">
          <div>
            <span>CATÁLOGO</span>

            <h2>Productos registrados</h2>

            <p>
              Consulta códigos, precios, impuestos y estado
              de cada producto.
            </p>
          </div>

          <button
            type="button"
            className="products-refresh-button"
            onClick={loadInitialData}
            disabled={busy}
          >
            <span aria-hidden="true">↻</span>
            Actualizar
          </button>
        </div>

        <div className="products-table-content">
          <DataTable
            data={products}
            columns={columns}
            onEdit={
              canManageProducts
                ? openEditModal
                : undefined
            }
            onToggleActive={
              canManageProducts
                ? toggleProductStatus
                : undefined
            }
          />
        </div>
      </section>

      {canManageProducts &&
        showEditModal &&
        editingProduct && (
          <div
            className="modal-overlay"
            onClick={closeEditModal}
          >
            <form
              className="product-edit-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="product-edit-title"
              onSubmit={saveProduct}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="modal-header">
                <h2 id="product-edit-title">
                  Editar ficha de producto
                </h2>
              </div>

              <div className="modal-body">
                <ProductFields
                  prefix="edit"
                  product={editingProduct}
                  categories={categories}
                  taxGroups={taxGroups}
                  onChange={handleEditChange}
                  taxInformation={editTaxInformation}
                  disabled={
                    busy || Boolean(catalogError)
                  }
                />
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="product-variants-button"
                  onClick={openVariantsModal}
                  disabled={busy}
                >
                  Gestionar variantes
                </button>

                <button
                  type="submit"
                  className="btn-save"
                  disabled={
                    busy || Boolean(catalogError)
                  }
                >
                  {isSaving
                    ? "Guardando..."
                    : "Guardar cambios"}
                </button>

                <button
                  type="button"
                  className="btn-cancel"
                  onClick={closeEditModal}
                  disabled={busy}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

      {canManageProducts &&
        selectedProductForVariants && (
          <ProductVariantsModal
            product={selectedProductForVariants}
            getHeaders={getVariantHeaders}
            onClose={() => {
              setSelectedProductForVariants(null);
              refreshProductsAfterMutation();
            }}
          />
        )}
    </div>
  );
}